import { useMemo } from 'react';

import {
	ExperienceName,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation';
import { performGqlFetch, useQuery } from '@atlassian/conversation-assistant-service';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private';
import { useSiteContext } from '../site-id';

import type { SitePermissionsResponse, SitePermissionsVariables } from './types';

const AgentPermissionsQuery = `
	query AgentPermissions($cloudId: ID!) {
		atlassianStudio_userSiteContext(cloudId: $cloudId) {
			... on AtlassianStudioUserSiteContextOutput {
				userPermissions {
					isAbleToCreateAgents
				}
			}
		}
	}
`;

type UseAgentSitePermissionsProps = {
	siteId?: string;
	touchpointSource: string;
	headers?: Record<string, string>;
};

export const useAgentSitePermissions = ({
	siteId: overrideSiteId,
	touchpointSource,
	headers,
}: UseAgentSitePermissionsProps) => {
	const { siteId } = useSiteContext();
	const experienceTracker = useAIMateExperienceTracker({
		touchpointSource,
	});

	const siteIdToUse = overrideSiteId ?? siteId;

	const query = useQuery({
		queryKey: ['fetchSitePermissions', siteIdToUse],
		queryFn: async () => {
			experienceTracker.start({
				id: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
			return await performGqlFetch<SitePermissionsResponse, SitePermissionsVariables>({
				gqlQuery: AgentPermissionsQuery,
				variables: {
					cloudId: siteIdToUse,
				},
				headerOptions: headers,
			});
		},
		onSuccess: () => {
			experienceTracker.succeed({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
		},
		onError: (error) => {
			experienceTracker.fail({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				error,
				teamName: ROVO_AGENTS_FE_TEAM_NAME,
			});
		},
	});

	return query;
};

// memoized wrapper around useAgentSitePermissions->isAbleToCreateAgents and its associated fgs
export const useCanCreateAgents = ({
	siteId,
	touchpointSource,
	headers,
}: UseAgentSitePermissionsProps) => {
	const { data: sitePermissions, isFetching: isFetchingSitePermissions } = useAgentSitePermissions({
		siteId,
		touchpointSource,
		headers,
	});

	const { isAbleToCreateAgents, isFetchingPermissions } = useMemo(() => {
		return {
			isAbleToCreateAgents:
				sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions
					?.isAbleToCreateAgents ?? false,
			isFetchingPermissions: isFetchingSitePermissions,
		};
	}, [
		isFetchingSitePermissions,
		sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
	]);

	return { isAbleToCreateAgents, isFetchingPermissions };
};
